/* eslint-disable brace-style */
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';

import { LocaleService } from '@next/core-lib/i18n';
import { Subscription } from 'rxjs';
import { map, startWith, filter } from 'rxjs/operators';

import { SoilType } from 'src/app/soil-type/soil-type.model';

import { Crop, Treatment, SalesOrderPeriod, TreatmentSchema, ProvinceEnum } from '../../models';

export interface CopyTreatmentsDialogResult {
  province: ProvinceEnum[];
  cropId: number;
  cropYears: number[];
  soilTypeIds: number[];
  treatmentIds: string[];
  schemaIds: string[];
  isSkalOptions: boolean[];
  isPlanetProofOptions: boolean[];
  isGroundWaterPreservationOptions: boolean[];
}

export interface CopyTreatmentsDialogData extends AbstractFormDialogData {
  crops: Crop[];
  soilTypes: SoilType[];
  cropYears: number[];
  treatments: Treatment[];
  treatmentSchemas: TreatmentSchema[];
  salesOrderPeriods: SalesOrderPeriod[];
}

@Component({
  selector: 'app-copy-treatments-dialog',
  templateUrl: './copy-treatments-dialog.component.html',
  standalone: false,
})
export class CopyTreatmentsDialogComponent
  extends AbstractFormDialog<CopyTreatmentsDialogResult>
  implements OnInit, OnDestroy
{
  data = inject<CopyTreatmentsDialogData>(MAT_DIALOG_DATA);
  private readonly locale = inject(LocaleService);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        CopyTreatmentsDialogComponent,
        AbstractDialogResultData<CopyTreatmentsDialogResult>
      >
    >(MatDialogRef);

  private subscription = new Subscription();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formGroup = this.formBuilder.group<any>({
    cropId: [undefined, Validators.required],
    cropYears: [[], Validators.required],
    selectAllTreatmentsAndSchema: [false],
    soilTypeIds: [[], Validators.required],
    treatmentIds: [[], Validators.required],
    schemaIds: [[]],
    isSkalOptions: [[], Validators.required],
    isPlanetProofOptions: [[], Validators.required],
    isGroundWaterPreservationOptions: [[], Validators.required],
    province: [[], Validators.required],
  });

  filteredCrops = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.filterCrops(value, [...this.data.crops])),
  );

  booleanOptions = [
    {
      key: this.locale.translate('yes'),
      value: true,
    },
    {
      key: this.locale.translate('no'),
      value: false,
    },
  ];
  areAllOptionsSelected = false;
  provinces = Object.values(ProvinceEnum);

  ngOnInit() {
    this.subscription.add(
      this.formGroup.get('schemaIds').valueChanges.subscribe((schemaIds: string[]) => {
        this.updateTreatmentSelection(schemaIds);
      }),
    );

    this.subscription.add(
      this.formGroup.get('isPlanetProofOptions').valueChanges.subscribe(() => {
        const schemaIds = this.formGroup.get('schemaIds').value as string[];
        this.updateTreatmentSelection(schemaIds);
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  updateTreatmentSelection(schemaIds: string[]) {
    let treatmentIds: string[] = [];

    const isAllTreatmentsSelected = this.formGroup.get('selectAllTreatmentsAndSchema').value;
    if (isAllTreatmentsSelected) {
      treatmentIds = this.data.treatments.map((treatment) => treatment.id);
    } else {
      schemaIds.forEach((schemaId) => {
        const schema = this.data.treatmentSchemas.find((s) => s.id === schemaId);
        treatmentIds = !schema ? treatmentIds : [...treatmentIds, ...schema.treatmentIds];
      });
    }

    treatmentIds = treatmentIds.filter(
      (item, index) =>
        treatmentIds.indexOf(item) === index && !this.isForbiddenForPlanetProof(item),
    );

    this.formGroup.patchValue({ treatmentIds });
  }

  displayCrop() {
    return (cropId?: number) =>
      cropId ? this.data.crops.find((crop) => crop.id === cropId).name : '';
  }

  getSalesOrderPeriod(salesOrderPeriods: SalesOrderPeriod[], periodId: number) {
    return salesOrderPeriods.find((salesOrderPeriod) => salesOrderPeriod.id === periodId);
  }

  private filterCrops(value: string, items: Crop[]): Crop[] {
    const filterValue = value.toLowerCase();
    return items
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  isInSelectedSchema(id: string): boolean {
    const selectedSchemaIds = this.formGroup.get('schemaIds').value as string[];

    if (!selectedSchemaIds || selectedSchemaIds.length === 0) {
      return false;
    }

    const selectedSchemas = this.data.treatmentSchemas.filter(
      (schema) => selectedSchemaIds.indexOf(schema.id) > -1,
    );
    const treatmentIds = selectedSchemas.map((schema) => schema.treatmentIds);

    return treatmentIds.reduce((a, b) => a.concat(b)).indexOf(id) > -1;
  }

  isForbiddenForPlanetProof(id: string): boolean {
    const isPlanetProofOptions = this.formGroup.get('isPlanetProofOptions').value as boolean[];
    const isPlanetProof = isPlanetProofOptions.some((o) => o === true);
    const treatment = this.data.treatments.find((t) => t.id.toString() === id);
    return isPlanetProof && treatment?.forbiddenForPlanetProof;
  }

  isSchemaForbiddenForPlanetProof(id: string): boolean {
    const isPlanetProofOptions = this.formGroup.get('isPlanetProofOptions').value as boolean[];
    const isPlanetProof = isPlanetProofOptions.some((o) => o === true);
    const schemaMatch = this.data.treatmentSchemas.find((schema) => schema.id === id);
    if (isPlanetProof && schemaMatch) {
      const treatments = this.data.treatments.filter(
        (t) => schemaMatch.treatmentIds.indexOf(t.id.toString()) > -1,
      );
      return treatments.some((t) => t.forbiddenForPlanetProof === true);
    }

    return false;
  }

  selectAllChanged(selectAllValue: boolean) {
    const provinceOptions = this.formGroup.get('province');
    if (selectAllValue) {
      provinceOptions.setValue(this.provinces);
    } else {
      provinceOptions.setValue([]);
    }

    const soilTypeOptions = this.formGroup.get('soilTypeIds');
    if (selectAllValue) {
      soilTypeOptions.setValue(this.data.soilTypes.map((soilType) => soilType.id));
    } else {
      soilTypeOptions.setValue([]);
    }

    const booleanOptions = [
      'isSkalOptions',
      'isPlanetProofOptions',
      'isGroundWaterPreservationOptions',
    ];
    booleanOptions.forEach((booleanOption) => {
      const options = this.formGroup.get(booleanOption);
      if (selectAllValue) {
        options.setValue([true, false]);
      } else {
        options.setValue([]);
      }
    });

    this.selectSchemasAndTreatments(selectAllValue);

    this.areAllOptionsSelected = selectAllValue;
  }

  selectSchemasAndTreatments(selectValue: boolean) {
    const schemaIdOptions = this.formGroup.get('schemaIds');
    if (selectValue) {
      schemaIdOptions.setValue(
        this.data.treatmentSchemas
          .filter((schema) => !this.isSchemaForbiddenForPlanetProof(schema.id))
          .map((schema) => schema.id),
      );
    } else {
      schemaIdOptions.setValue([]);
    }

    const treatmentIdOptions = this.formGroup.get('treatmentIds');
    if (selectValue) {
      treatmentIdOptions.setValue(
        this.data.treatments
          .filter((treatment) => !this.isForbiddenForPlanetProof(treatment.id))
          .map((treatment) => treatment.id),
      );
    } else {
      treatmentIdOptions.setValue([]);
    }
  }
}
